import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Chart from 'chart.js/auto';
import AdComponent from './AdComponent';
import '../css/LinkDetails.css';

const normalizeReferrer = (value = '') => {
  if (!value || value === 'Direct') return 'Direct';
  let host = value;
  try {
    host = new URL(value).hostname;
  } catch (error) {
    host = value;
  }
  host = host.replace(/^www\./, '').toLowerCase();
  if (host.includes('facebook.com') || host.includes('fb.com') || host.includes('l.facebook.com')) return 'Facebook';
  if (host.includes('instagram.com')) return 'Instagram';
  if (host.includes('tiktok.com')) return 'TikTok';
  if (host.includes('twitter.com') || host.includes('t.co') || host.includes('x.com')) return 'X (Twitter)';
  if (host.includes('linkedin.com') || host.includes('lnkd.in')) return 'LinkedIn';
  if (host.includes('reddit.com')) return 'Reddit';
  if (host.includes('youtube.com') || host.includes('youtu.be')) return 'YouTube';
  if (host.includes('news.google.com')) return 'Google News';
  if (host.includes('google.')) return 'Google';
  if (host.includes('bing.com')) return 'Bing';
  if (host.includes('duckduckgo.com')) return 'DuckDuckGo';
  if (host.includes('yahoo.com')) return 'Yahoo';
  if (host.includes('direct')) return 'Direct';
  return host;
};

const LinkDetails = () => {
  const { shortUrl } = useParams();
  const navigate = useNavigate();
  const [linkDetails, setLinkDetails] = useState(null);
  const [error, setError] = useState('');
  const [rangeDays, setRangeDays] = useState(30);
  const lineChartRef = useRef(null);
  const deviceChartRef = useRef(null);
  const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://urlshortener-serverside.onrender.com/api';

  useEffect(() => {
    const fetchLinkDetails = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/links/${shortUrl}/details`);
        setLinkDetails(response.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to fetch link details.');
      }
    };
    fetchLinkDetails();
  }, [BASE_URL, shortUrl]);

  const analytics = useMemo(() => {
    if (!linkDetails) return null;
    const now = Date.now();
    const rangeMs = rangeDays * 24 * 60 * 60 * 1000;
    const cutoff = now - rangeMs;
    const prevCutoff = cutoff - rangeMs;

    const events = (linkDetails.clickTimestamps || [])
      .map((event) => ({
        ...event,
        ts: new Date(event.timestamp).getTime(),
      }))
      .sort((a, b) => a.ts - b.ts);

    const filtered = events.filter((e) => e.ts >= cutoff);
    const prevFiltered = events.filter((e) => e.ts >= prevCutoff && e.ts < cutoff);

    const byDay = {};
    filtered.forEach((e) => {
      const label = new Date(e.ts).toLocaleDateString();
      byDay[label] = (byDay[label] || 0) + 1;
    });
    const clicksLabels = Object.keys(byDay);
    const clicksData = clicksLabels.map((k) => byDay[k]);

    const totalClicks = filtered.length;
    const uniqueIps = new Set(filtered.map((e) => e.ip).filter(Boolean));
    const countries = {};
    const referrers = {};
    const devices = {};
    const browsers = {};
    filtered.forEach((e) => {
      const country = e.country || 'Unknown';
      const ref = normalizeReferrer(e.referrer);
      const device = e.device || 'desktop';
      const browser = e.browser || 'Unknown';
      countries[country] = (countries[country] || 0) + 1;
      referrers[ref] = (referrers[ref] || 0) + 1;
      devices[device] = (devices[device] || 0) + 1;
      browsers[browser] = (browsers[browser] || 0) + 1;
    });

    const topCountryEntry = Object.entries(countries).sort((a, b) => b[1] - a[1])[0];
    const topReferrerEntry = Object.entries(referrers).sort((a, b) => b[1] - a[1])[0];
    const topDeviceEntry = Object.entries(devices).sort((a, b) => b[1] - a[1])[0];
    const topBrowserEntry = Object.entries(browsers).sort((a, b) => b[1] - a[1])[0];
    const peakDayEntry = Object.entries(byDay).sort((a, b) => b[1] - a[1])[0];

    const prevClicks = prevFiltered.length;
    let trend = { label: '0% vs prev', direction: 'flat' };
    if (prevClicks === 0 && totalClicks === 0) {
      trend = { label: 'No change', direction: 'flat' };
    } else if (prevClicks === 0) {
      trend = { label: 'New', direction: 'up' };
    } else if (totalClicks === prevClicks) {
      trend = { label: '0% vs prev', direction: 'flat' };
    } else {
      const change = totalClicks - prevClicks;
      const pct = Math.round((Math.abs(change) / prevClicks) * 100);
      trend = {
        label: `${change > 0 ? '+' : '-'}${pct}% vs prev`,
        direction: change > 0 ? 'up' : 'down',
      };
    }

    return {
      filtered,
      clicksLabels,
      clicksData,
      totalClicks,
      prevClicks,
      trend,
      avgDailyClicks: rangeDays ? Number((totalClicks / rangeDays).toFixed(1)) : 0,
      lastActivity: filtered.length ? new Date(filtered[filtered.length - 1].ts).toLocaleString() : 'No recent activity',
      uniqueVisitors: uniqueIps.size,
      topCountry: topCountryEntry?.[0] || 'Unknown',
      topReferrer: topReferrerEntry?.[0] || 'Direct',
      topDevice: topDeviceEntry?.[0] || 'Unknown',
      topBrowser: topBrowserEntry?.[0] || 'Unknown',
      peakDay: peakDayEntry ? { label: peakDayEntry[0], count: peakDayEntry[1] } : null,
      countries: Object.entries(countries).sort((a, b) => b[1] - a[1]).slice(0, 6),
      referrers: Object.entries(referrers).sort((a, b) => b[1] - a[1]).slice(0, 6),
      devices,
      browsers: Object.entries(browsers).sort((a, b) => b[1] - a[1]).slice(0, 5),
    };
  }, [linkDetails, rangeDays]);

  useEffect(() => {
    if (!analytics) return;
    const lineEl = document.getElementById('clicksOverTimeChart');
    const pieEl = document.getElementById('deviceBreakdownChart');
    if (!lineEl || !pieEl) return;

    if (lineChartRef.current) lineChartRef.current.destroy();
    if (deviceChartRef.current) deviceChartRef.current.destroy();

    lineChartRef.current = new Chart(lineEl.getContext('2d'), {
      type: 'line',
      data: {
        labels: analytics.clicksLabels,
        datasets: [
          {
            label: 'Clicks',
            data: analytics.clicksData,
            borderColor: '#1f74d4',
            backgroundColor: 'rgba(31,116,212,0.18)',
            fill: true,
            tension: 0.35,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#0f172a',
            padding: 10,
            titleFont: { size: 12, weight: '600' },
            bodyFont: { size: 12 },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: '#60708b' },
          },
          y: {
            grid: { color: 'rgba(15, 23, 42, 0.08)' },
            ticks: { color: '#60708b', precision: 0 },
          },
        },
      },
    });

    const deviceEntries = Object.entries(analytics.devices);
    if (!deviceEntries.length) return;
    deviceChartRef.current = new Chart(pieEl.getContext('2d'), {
      type: 'doughnut',
      data: {
        labels: deviceEntries.map(([name]) => name),
        datasets: [
          {
            data: deviceEntries.map(([, count]) => count),
            backgroundColor: ['#1f74d4', '#13b184', '#f59e0b', '#7c3aed'],
            borderWidth: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '62%',
        plugins: { legend: { position: 'right', labels: { color: '#60708b', boxWidth: 10 } } },
      },
    });
  }, [analytics]);

  if (error) return <div className="error">{error}</div>;
  if (!linkDetails || !analytics) return <div className="loading">Loading...</div>;

  const rangeLabel = rangeDays === 7 ? 'Last 7 Days' : rangeDays === 30 ? 'Last 30 Days' : 'Last 90 Days';

  return (
    <div className="link-details-shell">
      <header className="link-details-hero">
        <div className="link-details-top">
          <button className="back-button" onClick={() => navigate(-1)}>&lt; Back</button>
          <div className="title-wrap">
            <p className="eyebrow">Link Analytics</p>
            <h1>bl-i.us/<span>{shortUrl}</span></h1>
          </div>
        </div>
        <div className="hero-meta">
          <div className="meta-item">
            <span>Range</span>
            <strong>{rangeLabel}</strong>
          </div>
          <div className="meta-item">
            <span>Last Activity</span>
            <strong>{analytics.lastActivity}</strong>
          </div>
          <div className={`status-pill ${analytics.totalClicks ? 'active' : 'idle'}`}>
            {analytics.totalClicks ? 'Active' : 'No recent clicks'}
          </div>
        </div>
      </header>

      <div className="link-details-bar">
        <div className="link-details-url">
          <span>Original URL</span>
          <a href={linkDetails.longUrl} target="_blank" rel="noopener noreferrer">{linkDetails.longUrl}</a>
        </div>
        <div className="range-select">
          <span>Time Range</span>
          <select value={rangeDays} onChange={(e) => setRangeDays(Number(e.target.value))}>
            <option value={7}>Last 7 Days</option>
            <option value={30}>Last 30 Days</option>
            <option value={90}>Last 90 Days</option>
          </select>
        </div>
      </div>

      <div className="metric-grid">
        <article className="metric-card">
          <div className="metric-label">Clicks (range)</div>
          <div className="metric-value">{analytics.totalClicks.toLocaleString()}</div>
          <div className={`metric-delta ${analytics.trend.direction}`}>{analytics.trend.label}</div>
        </article>
        <article className="metric-card accent">
          <div className="metric-label">All-time Clicks</div>
          <div className="metric-value">{(linkDetails.clicks || 0).toLocaleString()}</div>
          <div className="metric-sub">Since launch</div>
        </article>
        <article className="metric-card">
          <div className="metric-label">Unique Visitors</div>
          <div className="metric-value">{analytics.uniqueVisitors.toLocaleString()}</div>
          <div className="metric-sub">In selected range</div>
        </article>
        <article className="metric-card">
          <div className="metric-label">Avg Daily Clicks</div>
          <div className="metric-value">{analytics.avgDailyClicks}</div>
          <div className="metric-sub">{rangeLabel}</div>
        </article>
        <article className="metric-card">
          <div className="metric-label">Top Source</div>
          <div className="metric-value small">{analytics.topReferrer}</div>
          <div className="metric-sub">Most common source</div>
        </article>
      </div>

      <section className="chart-panel">
        <div className="panel-header">
          <div>
            <h3>Clicks Over Time</h3>
            <p>{analytics.totalClicks.toLocaleString()} clicks in {rangeLabel.toLowerCase()}</p>
          </div>
          <div className="panel-badge">
            {analytics.peakDay ? `Peak: ${analytics.peakDay.label} (${analytics.peakDay.count})` : 'No data yet'}
          </div>
        </div>
        <div className="chart-canvas">
          <canvas id="clicksOverTimeChart" />
        </div>
      </section>

      <section className="details-grid">
        <article className="details-card">
          <h3>Traffic Sources</h3>
          {analytics.referrers.length ? (
            <ul className="stat-list">
              {analytics.referrers.map(([name, count]) => {
                const pct = analytics.totalClicks ? Math.round((count / analytics.totalClicks) * 100) : 0;
                return (
                  <li key={name}>
                    <div className="stat-row">
                      <span className="stat-name">{name}</span>
                      <span className="stat-count">{count}</span>
                    </div>
                    <div className="stat-bar"><span style={{ width: `${pct}%` }} /></div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="empty-state">No referrer data yet.</p>
          )}
        </article>

        <article className="details-card">
          <h3>Device Breakdown</h3>
          {Object.keys(analytics.devices).length ? (
            <div className="chart-canvas compact">
              <canvas id="deviceBreakdownChart" />
            </div>
          ) : (
            <p className="empty-state">No device data yet.</p>
          )}
        </article>

        <article className="details-card">
          <h3>Geographic Stats</h3>
          {analytics.countries.length ? (
            <ul className="stat-list">
              {analytics.countries.map(([name, count]) => {
                const pct = analytics.totalClicks ? Math.round((count / analytics.totalClicks) * 100) : 0;
                return (
                  <li key={name}>
                    <div className="stat-row">
                      <span className="stat-name">{name}</span>
                      <span className="stat-count">{count}</span>
                    </div>
                    <div className="stat-bar"><span style={{ width: `${pct}%` }} /></div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="empty-state">No location data yet.</p>
          )}
        </article>
      </section>

      <section className="browser-row">
        <h3>Top Browsers</h3>
        <div className="browser-chips">
          {analytics.browsers.length ? (
            analytics.browsers.map(([name, count]) => (
              <span key={name}>{name} {count}</span>
            ))
          ) : (
            <span className="empty-chip">No browser data yet</span>
          )}
        </div>
      </section>

      <section className="analytics-ad-row">
        <AdComponent containerId={`taboola-analytics-${shortUrl}`} />
      </section>
    </div>
  );
};

export default LinkDetails;
